import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPenToSquare,
  faTrashAlt,
  faChevronDown,
  faChevronRight,
  faCheck,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "react-modal";
import { ToastContainer, toast } from "react-toastify";

Modal.setAppElement("#root");

const roles = {
  8: "Администратор",
  4: "Сводящий",
  2: "Заведующий",
  1: "Адвокат",
};

export default function DivisionsTable({ divisions, users }) {
  const [divisionList, setDivisionList] = useState(divisions);
  const [newDivision, setNewDivision] = useState({ name: "", manager_id: "" });
  const [editingId, setEditingId] = useState(null);
  const [editingBackup, setEditingBackup] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [divisionToDelete, setDivisionToDelete] = useState(null);
  const [expandedDivisions, setExpandedDivisions] = useState([]);
  const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const toggleDivision = (id) => {
    setExpandedDivisions((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]
    );
  };

  const handleInputChange = (e, id) => {
    const { name, value } = e.target;
    const updatedDivisions = divisionList.map((division) =>
      division.id === id ? { ...division, [name]: value } : division
    );
    setDivisionList(updatedDivisions);
  };

  const handleAddDivision = async () => {
    const response = await fetch("/settings/add_divisions", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(newDivision),
    });
    if (response.ok) {
      const addedDivision = await response.json();
      setDivisionList([...divisionList, addedDivision]);
      setNewDivision({ name: "", manager_id: "" });
      toast.success("Подразделение успешно добавлено!");
    } else {
      toast.error("Ошибка при добавлении подразделения.");
    }
  };

  const handleEditDivision = async (division) => {
    const response = await fetch("/settings/update_divisions", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(division),
    });

    if (response.ok) {
      const updatedDivision = await response.json();

      const updatedDivisionList = divisionList.map((d) =>
        d.id === updatedDivision.id
          ? {
              ...updatedDivision,
              users: d.users || [],
            }
          : d
      );

      setDivisionList(updatedDivisionList);
      setEditingId(null);
      setEditingBackup(null);
      toast.success("Изменения сохранены!");
    } else {
      toast.error("Ошибка при сохранении изменений.");
    }
  };

  const handleCancelEdit = () => {
    if (editingBackup) {
      const restoredDivisions = divisionList.map((division) =>
        division.id === editingBackup.id ? editingBackup : division
      );
      setDivisionList(restoredDivisions);
    }
    setEditingId(null);
    setEditingBackup(null);
  };

  const startEditing = (division) => {
    setEditingId(division.id);
    setEditingBackup({ ...division });
  };

  const openDeleteModal = (id) => {
    setDivisionToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDivisionToDelete(null);
  };

  const handleDeleteDivision = async () => {
    const response = await fetch("/settings/delete_divisions", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: divisionToDelete }),
    });

    if (response.ok) {
      setDivisionList(divisionList.filter((d) => d.id !== divisionToDelete));
      closeDeleteModal();
      toast.success("Подразделение удалено!");
    } else {
      toast.error("Ошибка при удалении подразделения.");
    }
  };

  const openEditUserModal = (user) => {
    setUserToEdit({ ...user });
    setIsModalOpen(true);
  };

  const handleEditUser = async () => {
    const response = await fetch("/settings/update_user", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(userToEdit),
    });

    if (response.ok) {
      const updatedUser = await response.json();

      setDivisionList((prev) =>
        prev.map((division) =>
          division.id === updatedUser.division_id
            ? {
                ...division,
                users: division.users.map((user) =>
                  user.id === updatedUser.id ? updatedUser : user
                ),
              }
            : division
        )
      );
      toast.success("Пользователь обновлен!");
      setIsModalOpen(false);
    } else {
      toast.error("Ошибка при обновлении пользователя.");
    }
  };

  const openDeleteUserModal = (userId) => {
    setUserToDelete(userId);
    setIsDeleteUserModalOpen(true);
  };

  // Закрыть модалку
  const closeDeleteUserModal = () => {
    setIsDeleteUserModalOpen(false);
    setUserToDelete(null);
  };

  // Подтверждение удаления пользователя
  const confirmDeleteUser = async () => {
    const response = await fetch("/settings/delete_user", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: userToDelete }),
    });

    if (response.ok) {
      setDivisionList((prev) =>
        prev.map((division) => ({
          ...division,
          users: division.users.filter((user) => user.id !== userToDelete),
        }))
      );
      toast.success("Пользователь удален!");
    } else {
      toast.error("Ошибка при удалении пользователя.");
    }

    closeDeleteUserModal();
  };

  const handleDeleteUser = async (userId) => {
    const response = await fetch("/settings/delete_user", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: userId }),
    });

    if (response.ok) {
      setDivisionList((prev) =>
        prev.map((division) => ({
          ...division,
          users: division.users.filter((user) => user.id !== userId),
        }))
      );
      toast.success("Пользователь удален!");
    } else {
      toast.error("Ошибка при удалении пользователя.");
    }
  };

  return (
    <div className="min-h-full bg-gray-50">
      <ToastContainer />
      <div className="pb-12">
        <header className="py-6">
          <div className="mx-auto container max-w-screen-2xl px-4">
            <h1 className="text-3xl font-semibold tracking-tight text-black">
              Подразделения
            </h1>
          </div>
        </header>
      </div>

      <main>
        <div className="mx-auto container max-w-screen-2xl px-4 pb-12">
          <div className="relative overflow-auto max-h-[600px] border rounded-lg">
            <table className="table-fixed w-full border-collapse bg-white text-left text-sm text-gray-700">
              <thead className="bg-gradient-to-r from-blue-700 to-blue-800 text-white">
                <tr>
                  <th className="px-6 py-4 font-medium"></th>
                  <th className="px-6 py-4 font-medium">ID</th>
                  <th className="px-6 py-4 font-medium">Наименование</th>
                  <th className="px-6 py-4 font-medium">Руководитель</th>
                  <th className="px-6 py-4 font-medium text-right">Действия</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 border-t border-gray-200">
                {/* Форма для добавления подразделения */}
                <tr className="bg-gray-100">
                  <td></td>
                  <td className="px-6 py-4">-</td>
                  <td className="px-6 py-4">
                    <input
                      type="text"
                      name="name"
                      placeholder="Новое наименование"
                      value={newDivision.name}
                      onChange={(e) =>
                        setNewDivision({ ...newDivision, name: e.target.value })
                      }
                      className="w-full p-1 border rounded"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <select
                      name="manager_id"
                      value={newDivision.manager_id}
                      onChange={(e) =>
                        setNewDivision({
                          ...newDivision,
                          manager_id: e.target.value,
                        })
                      }
                      className="w-full p-1 border rounded"
                    >
                      <option value="">Выберите руководителя</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.realname}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={handleAddDivision}
                      className="bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                    >
                      Добавить
                    </button>
                  </td>
                </tr>

                {divisionList.map((division) => (
                  <>
                    <tr key={division.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <button onClick={() => toggleDivision(division.id)}>
                          <FontAwesomeIcon
                            icon={
                              expandedDivisions.includes(division.id)
                                ? faChevronDown
                                : faChevronRight
                            }
                          />
                        </button>
                      </td>
                      <td className="px-6 py-4 text-gray-900">{division.id}</td>
                      <td className="px-6 py-4">
                        {editingId === division.id ? (
                          <input
                            type="text"
                            name="name"
                            value={division.name}
                            onChange={(e) => handleInputChange(e, division.id)}
                            className="w-full p-1 border rounded"
                          />
                        ) : (
                          division.name.trim()
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {editingId === division.id ? (
                          <select
                            name="manager_id"
                            value={division.manager_id || ""}
                            onChange={(e) => handleInputChange(e, division.id)}
                            className="w-full p-1 border rounded"
                          >
                            <option value="">Выберите руководителя</option>
                            {users.map((user) => (
                              <option key={user.id} value={user.id}>
                                {user.realname}
                              </option>
                            ))}
                          </select>
                        ) : (
                          division.manager?.realname || "Нет руководителя"
                        )}
                      </td>
                      <td className="px-6 py-4 text-right space-x-2">
                        {editingId === division.id ? (
                          <>
                            <button
                              className="text-green-600 hover:text-green-800"
                              onClick={() => handleEditDivision(division)}
                            >
                              <FontAwesomeIcon icon={faCheck} size="lg" />
                            </button>
                            <button
                              className="text-gray-600 hover:text-gray-800"
                              onClick={handleCancelEdit}
                            >
                              <FontAwesomeIcon icon={faTimes} size="lg" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="text-blue-600 hover:text-blue-800 mr-4"
                              onClick={() => startEditing(division)}
                            >
                              <FontAwesomeIcon icon={faPenToSquare} size="lg" />
                            </button>
                            <button
                              className="text-red-600 hover:text-red-800"
                              onClick={() => openDeleteModal(division.id)}
                            >
                              <FontAwesomeIcon icon={faTrashAlt} size="lg" />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                    {expandedDivisions.includes(division.id) &&
                      division.users.map((user) => (
                        <tr
                          key={user.id}
                          className="bg-gray-100 hover:bg-gray-200"
                        >
                          <td></td>
                          <td></td>
                          <td className="px-6 py-4">{user.realname}</td>
                          <td>
                            <button
                              className="text-blue-500 hover:text-blue-700"
                              onClick={() => openEditUserModal(user)}
                            >
                              Редактировать
                            </button>
                          </td>
                          <td>
                            <button
                              className="text-red-500 hover:text-red-700"
                              onClick={() => openDeleteUserModal(user.id)}
                            >
                              Удалить
                            </button>
                          </td>
                        </tr>
                      ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Модальное окно для удаления подразделения */}
      {isDeleteModalOpen && (
        <Modal
          isOpen={isDeleteModalOpen}
          onRequestClose={closeDeleteModal}
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
          overlayClassName="fixed inset-0 bg-black bg-opacity-50"
        >
          <div className="bg-white p-6 rounded-lg shadow-lg w-96">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Вы уверены, что хотите удалить это подразделение?
            </h2>
            <div className="flex justify-end gap-4">
              <button
                onClick={closeDeleteModal}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                Отмена
              </button>
              <button
                onClick={handleDeleteDivision}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Удалить
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Модальное окно для редактирования пользователя */}
      {isModalOpen && userToEdit && (
        <Modal
          isOpen={isModalOpen}
          onRequestClose={() => setIsModalOpen(false)}
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
          overlayClassName="fixed inset-0 bg-black bg-opacity-50"
        >
          <div className="bg-white p-6 rounded-lg shadow-lg w-96">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Редактирование пользователя
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Имя пользователя
                </label>
                <input
                  type="text"
                  value={userToEdit.realname}
                  onChange={(e) =>
                    setUserToEdit((prev) => ({
                      ...prev,
                      realname: e.target.value,
                    }))
                  }
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Логин
                </label>
                <input
                  type="text"
                  value={userToEdit.username}
                  onChange={(e) =>
                    setUserToEdit((prev) => ({
                      ...prev,
                      username: e.target.value,
                    }))
                  }
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  value={userToEdit.email || ""}
                  onChange={(e) =>
                    setUserToEdit((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Пароль
                </label>
                <input
                  type="password"
                  value={userToEdit.password || ""}
                  onChange={(e) =>
                    setUserToEdit((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Подтверждение пароля
                </label>
                <input
                  type="password"
                  value={userToEdit.confirm_password || ""}
                  onChange={(e) =>
                    setUserToEdit((prev) => ({
                      ...prev,
                      confirm_password: e.target.value,
                    }))
                  }
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Роль
                </label>
                <select
                  value={userToEdit.role}
                  onChange={(e) =>
                    setUserToEdit((prev) => ({ ...prev, role: e.target.value }))
                  }
                  className="w-full p-2 border rounded"
                >
                  {Object.entries(roles).map(([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Подразделение
                </label>
                <select
                  value={userToEdit.division_id}
                  onChange={(e) =>
                    setUserToEdit((prev) => ({
                      ...prev,
                      division_id: e.target.value,
                    }))
                  }
                  className="w-full p-2 border rounded"
                >
                  {divisionList.map((division) => (
                    <option key={division.id} value={division.id}>
                      {division.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={handleEditUser}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
              >
                Сохранить
              </button>
              <button
                onClick={() => setIsModalOpen(false)}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
              >
                Отмена
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Модальное окно для подтверждения удаления пользователя */}
      {isDeleteUserModalOpen && (
        <Modal
          isOpen={isDeleteUserModalOpen}
          onRequestClose={closeDeleteUserModal}
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
          overlayClassName="fixed inset-0 bg-black bg-opacity-50"
        >
          <div className="bg-white p-6 rounded-lg shadow-lg w-96">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Вы уверены, что хотите удалить этого пользователя?
            </h2>
            <div className="flex justify-end gap-4">
              <button
                onClick={closeDeleteUserModal}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                Отмена
              </button>
              <button
                onClick={confirmDeleteUser}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Удалить
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
